import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { Observable, map } from 'rxjs';
import { MapStyle, MapDataSetLayer, MapConfig } from '../shared/models';
import { MapStateService } from './map-state.service';
import { MapboxService } from './mapbox-map/mapbox-map.service';

import * as _ from 'lodash';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { MapDrawControlsComponent } from './map-draw-controls/map-draw-controls.component';
import { MapStyleControlsComponent } from './map-style-controls/map-style-controls.component';
import { MapboxMapComponent } from './mapbox-map/mapbox-map.component';
import { TranslateModule } from '@ngx-translate/core';
import { SpinnerComponent } from '../shared/components/spinner/spinner.component';
import { AsyncPipe } from '@angular/common';
import { AppStateService } from '@modules/core/services/app-state.service';
import { APP_CONTEXT } from '@modules/core/models/app-state';

@Component({
    selector: 'places-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    imports: [
        AsyncPipe,
        SpinnerComponent,
        TranslateModule,
        MapboxMapComponent,
        MapStyleControlsComponent,
        MapDrawControlsComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class MapComponent implements OnInit {
    private readonly _mapState = inject(MapStateService);
    private readonly _mapboxService = inject(MapboxService);
    private readonly _appService = inject(AppStateService);

    public isLoading = true;
    public mapStyles: MapStyle[] = [];
    public defaultMapStyle: MapStyle;
    public defaultCenter: [number, number];
    public isEditTrafficDeviationContext$: Observable<boolean>;


    constructor() {
        this._mapState.init();
    }

    ngOnInit() {
        this.isLoading = false;
        this._setMapConfigs();
        this.isEditTrafficDeviationContext$ = this._appService.getPropertyState('selectedContext').pipe(
            map((context) => context === APP_CONTEXT.EDIT_TRAFFIC_DEVIATION_TEMPLATES)
        );
    }

    private _setMapConfigs() {
        // sets the default mapStyles
        const config: MapConfig = this._mapConfig();

        this.mapStyles = config.styles || [];

        const defaultMapStyle = config.getDefaultMapStyle();

        if (defaultMapStyle && this.defaultMapStyle !== defaultMapStyle) {
            // change the style on the map
            this.defaultMapStyle = defaultMapStyle;
        }

        // get the layer that should be drawn
        const layersToDraw: MapDataSetLayer[] = config.dataSetLayers || [];
        this._mapboxService.setLayersToDraw(layersToDraw);
    }

    private _mapConfig(): MapConfig {
        return new MapConfig({
            styles: [
                {
                    default: true,
                    name: _translate('Light'),
                    type: 'mapbox',
                    icon: 'map-light.png',
                    config: {
                        style: 'mapbox://styles/nobinadevelopment/cl3k73cxm003g14p8vrfoohda'
                    }
                },
                {
                    default: false,
                    name: _translate('Dark'),
                    type: 'mapbox',
                    icon: 'map-dark.png',
                    theme: 'dark',
                    config: {
                        style: 'mapbox://styles/nobinadevelopment/cl3k76sey001h15qlq3mtjwqn'
                    }
                },
                {
                    default: false,
                    name: _translate('Satellite'),
                    type: 'mapbox',
                    icon: 'map-orto.png',
                    theme: 'dark',
                    config: {
                        style: 'mapbox://styles/nobinadevelopment/cl3k75sba004i16mr9p21qdzf'
                    }
                }
            ]
        });
    }
}
